<template>
  <div class="fac-card">
    <header class="fac-header">
      <div class="fac-title-block">
        <span class="fac-title">
          <i class="bx bx-bar-chart-square fac-title-icon"></i>
          Análise por Campo
          <span v-if="primary" class="fac-field-chip fac-field-chip-primary">
            {{ primaryLabel }}
          </span>
          <span v-if="secondary" class="fac-cross-arrow">
            <i class="bx bx-x"></i>
          </span>
          <span v-if="secondary" class="fac-field-chip fac-field-chip-secondary">
            {{ secondaryLabel }}
          </span>
        </span>
        <p v-if="!primary" class="fac-subtitle">
          Distribuição dos valores entre os participantes que fizeram check-in.
        </p>
      </div>
      <div v-if="primary" class="fac-header-right">
        <button
          class="fac-change-btn"
          @click="openPrimaryPicker"
          title="Trocar campo principal"
        >
          <i class="bx bx-transfer-alt"></i>
        </button>
      </div>
    </header>

    <!-- Empty state — placeholder clicável -->
    <div v-if="!primary" class="fac-placeholder" @click="openPrimaryPicker">
      <div class="fac-placeholder-icon">
        <i class="bx bx-stats"></i>
      </div>
      <div class="fac-placeholder-text">Clique para escolher um campo</div>
      <div class="fac-placeholder-sub">
        Veja como os participantes que fizeram check-in se distribuem
        em qualquer campo do evento — cidade, cargo, grupo, qualquer
        pergunta do formulário.
      </div>
    </div>

    <!-- Loading -->
    <div v-else-if="isLoading" class="fac-loading">
      <i class="bx bx-loader-alt fac-spin"></i>
      <span>Carregando…</span>
    </div>

    <!-- Sem dados -->
    <div v-else-if="!hasRows" class="fac-empty">
      <div class="fac-empty-icon">
        <i class="bx bx-data"></i>
      </div>
      <div class="fac-empty-title">Sem dados para este campo</div>
      <div class="fac-empty-desc">
        Nenhum participante com check-in tem este campo preenchido no período filtrado.
      </div>
    </div>

    <!-- Single mode: ranking de valores do campo primário -->
    <div v-else-if="!secondary" class="fac-chart-wrap">
      <div class="fac-chart-meta">
        <span class="fac-total-badge">
          <i class="bx bx-group"></i>
          {{ formatTotal(data.total) }} respostas no período
        </span>
        <button
          class="fac-cross-btn"
          @click="openSecondaryPicker"
          title="Compare este campo com outro"
        >
          <i class="bx bx-shuffle"></i>
          Cruzar com outro campo
        </button>
      </div>

      <div class="fac-ranking">
        <div
          v-for="(row, i) in topRows"
          :key="row.value + i"
          class="fac-rank-row"
          :style="{ animationDelay: (i * 40) + 'ms' }"
        >
          <div class="fac-rank-num" :class="rankClass(i)">{{ i + 1 }}</div>
          <div class="fac-rank-body">
            <div class="fac-rank-label" :title="row.value">{{ row.value }}</div>
            <div class="fac-rank-bar-track">
              <div
                class="fac-rank-bar-fill"
                :style="{ width: barWidth(row.count, chartMax) }"
              ></div>
            </div>
          </div>
          <div class="fac-rank-right">
            <span class="fac-rank-count">{{ row.count.toLocaleString('pt-BR') }}</span>
            <span class="fac-rank-pct">{{ rowPct(row.count) }}%</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Cross mode: ranking hierárquico (primário > secundário) -->
    <div v-else class="fac-chart-wrap">
      <div class="fac-chart-meta">
        <span class="fac-total-badge">
          <i class="bx bx-group"></i>
          {{ formatTotal(data.total) }} respostas em
          {{ groupedRows.length }} {{ groupedRows.length === 1 ? 'grupo' : 'grupos' }}
        </span>
        <button
          class="fac-cross-btn fac-cross-btn-active"
          @click="onClearSecondary"
          title="Voltar para visão simples"
        >
          <i class="bx bx-x"></i>
          Remover cruzamento
        </button>
      </div>

      <div class="fac-cross-list">
        <div
          v-for="(group, gIdx) in groupedRows"
          :key="group.primary + gIdx"
          class="fac-cross-group"
          :style="{ animationDelay: (gIdx * 60) + 'ms' }"
        >
          <div class="fac-cross-group-head">
            <span class="fac-cross-group-rank" :class="rankClass(gIdx)">{{ gIdx + 1 }}</span>
            <span class="fac-cross-group-name" :title="group.primary">{{ group.primary }}</span>
            <span class="fac-cross-group-count">{{ group.total }}</span>
            <span class="fac-cross-group-pct">{{ groupPct(group.total) }}%</span>
          </div>

          <div class="fac-cross-children">
            <div
              v-for="(child, cIdx) in group.items.slice(0, 6)"
              :key="child.secondary_value + cIdx"
              class="fac-cross-child-row"
            >
              <span class="fac-cross-child-label" :title="child.secondary_value">{{ child.secondary_value }}</span>
              <div class="fac-cross-child-bar">
                <div
                  class="fac-cross-child-fill"
                  :style="{ width: barWidth(child.count, group.max) }"
                ></div>
              </div>
              <span class="fac-cross-child-count">{{ child.count }}</span>
              <span class="fac-cross-child-pct">{{ groupChildPct(child.count, group.total) }}%</span>
            </div>
            <div v-if="group.items.length > 6" class="fac-cross-more">
              + {{ group.items.length - 6 }} {{ group.items.length - 6 === 1 ? 'outra' : 'outras' }} {{ secondaryLabel.toLowerCase() }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal picker (compartilhado entre primary e secondary) -->
    <div v-if="showPicker" class="fac-picker-overlay" @click.self="closePicker">
      <div class="fac-picker">
        <div class="fac-picker-header">
          <div class="fac-picker-header-left">
            <i class="bx bx-search-alt-2 fac-picker-header-icon"></i>
            <span class="fac-picker-title">{{ pickerTitle }}</span>
          </div>
          <button class="fac-picker-close" @click="closePicker">
            <i class="bx bx-x"></i>
          </button>
        </div>

        <div v-if="pickerMode === 'secondary' && primary" class="fac-picker-context">
          <span class="fac-picker-context-label">Campo principal</span>
          <span class="fac-field-chip fac-field-chip-primary fac-picker-context-chip">
            {{ primaryLabel }}
          </span>
        </div>

        <div class="fac-picker-body">
          <div
            v-for="group in pickerFieldsByGroup"
            :key="group.name"
            class="fac-group"
          >
            <div class="fac-group-label">
              <i :class="groupIcon(group.name)"></i>
              {{ group.name }}
            </div>
            <div class="fac-fields-grid">
              <button
                v-for="f in group.items"
                :key="fieldKey(f)"
                class="fac-field-btn"
                :class="{ selected: isFieldSelectedInPicker(f) }"
                @click="onPickField(f)"
              >
                <span class="fac-field-btn-label">{{ f.label }}</span>
                <i v-if="isFieldSelectedInPicker(f)" class="bx bx-check fac-field-check"></i>
              </button>
            </div>
          </div>
        </div>

        <div v-if="hasSelectionInPicker" class="fac-picker-footer">
          <button class="fac-clear-btn" @click="onClearInPicker">
            <i class="bx bx-trash"></i>
            {{ pickerMode === 'secondary' ? 'Remover cruzamento' : 'Limpar seleção' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const CACHE_KEY = 'cd_field_analysis_selected'
const CACHE_KEY_SEC = 'cd_field_analysis_secondary'

export default {
  name: 'field-analysis-card',
  props: {
    primary: { type: Object, default: null }, // { source, id }
    secondary: { type: Object, default: null }, // { source, id }
    data: { type: Object, default: null },
    isLoading: { type: Boolean, default: false },
    fields: { type: Array, default: () => [] }
  },
  data: () => ({
    showPicker: false,
    pickerMode: 'primary' // 'primary' | 'secondary'
  }),
  computed: {
    primaryKey () {
      return this.primary ? this.fieldKey(this.primary) : ''
    },
    secondaryKey () {
      return this.secondary ? this.fieldKey(this.secondary) : ''
    },
    primaryLabel () {
      if (!this.primary) return ''
      const f = this.fields.find(x => this.fieldKey(x) === this.primaryKey)
      return f ? f.label : ''
    },
    secondaryLabel () {
      if (!this.secondary) return ''
      const f = this.fields.find(x => this.fieldKey(x) === this.secondaryKey)
      return f ? f.label : ''
    },
    fieldsByGroup () {
      return this.groupFields(this.fields)
    },
    /**
     * Quando picker está em modo "secondary", filtra o campo primário fora
     * da lista — não faz sentido cruzar campo com ele mesmo.
     */
    pickerFieldsByGroup () {
      if (this.pickerMode === 'secondary' && this.primary) {
        const filtered = this.fields.filter(f => this.fieldKey(f) !== this.primaryKey)
        return this.groupFields(filtered)
      }
      return this.fieldsByGroup
    },
    pickerTitle () {
      return this.pickerMode === 'secondary'
        ? 'Cruzar com qual campo?'
        : 'Escolher campo para análise'
    },
    hasSelectionInPicker () {
      return this.pickerMode === 'secondary' ? !!this.secondary : !!this.primary
    },
    rows () {
      return (this.data && this.data.rows) || []
    },
    hasRows () {
      return this.rows.length > 0
    },
    topRows () {
      return this.rows.slice(0, 12)
    },
    chartMax () {
      return this.rows[0] ? this.rows[0].count || 1 : 1
    },
    /**
     * Para modo cross — agrupa rows por valor primário, mantendo a ordem
     * (backend já vem ordenado por count desc). Cada grupo tem max próprio
     * pra que as barras dos children sejam relativas ao topo do grupo.
     */
    groupedRows () {
      const map = new Map()
      const groups = []
      this.rows.forEach(row => {
        const key = row.primary_value
        let g = map.get(key)
        if (!g) {
          g = { primary: key, items: [], total: 0, max: 0 }
          map.set(key, g)
          groups.push(g)
        }
        g.items.push(row)
        g.total += row.count
        if (row.count > g.max) g.max = row.count
      })
      groups.sort((a, b) => b.total - a.total)
      return groups.slice(0, 8)
    }
  },
  mounted () {
    // Restaura seleção do localStorage SE não houver primary já vindo do store.
    if (!this.primary) {
      try {
        const cached = JSON.parse(localStorage.getItem(CACHE_KEY))
        if (cached && cached.source && cached.id) {
          this.$emit('change-primary', { source: cached.source, id: cached.id })
        }
      } catch (e) { /* ignora cache corrompido */ }
    }
    // Mesmo pra secondary
    if (!this.secondary) {
      try {
        const cached = JSON.parse(localStorage.getItem(CACHE_KEY_SEC))
        if (cached && cached.source && cached.id) {
          this.$emit('change-secondary', { source: cached.source, id: cached.id })
        }
      } catch (e) { /* ignora */ }
    }
  },
  methods: {
    fieldKey (f) {
      return f ? `${f.source}::${f.id}` : ''
    },
    groupFields (fields) {
      const groups = new Map()
      fields.forEach(f => {
        const g = f.group || 'Outros'
        if (!groups.has(g)) groups.set(g, { name: g, items: [] })
        groups.get(g).items.push(f)
      })
      return Array.from(groups.values())
    },
    rankClass (i) {
      if (i === 0) return 'fac-rank-num--gold'
      if (i === 1) return 'fac-rank-num--silver'
      if (i === 2) return 'fac-rank-num--bronze'
      return ''
    },
    groupIcon (name) {
      if (name && name.toLowerCase().includes('formul')) return 'bx bx-edit-alt'
      if (name && name.toLowerCase().includes('inscri')) return 'bx bx-category'
      return 'bx bx-user'
    },
    barWidth (count, max) {
      const pct = Math.max(2, Math.round((count / Math.max(1, max)) * 100))
      return `${pct}%`
    },
    rowPct (count) {
      const total = this.data && this.data.total ? this.data.total : 1
      return Math.round((count / total) * 100)
    },
    groupPct (groupTotal) {
      const total = this.data && this.data.total ? this.data.total : 1
      return Math.round((groupTotal / total) * 100)
    },
    groupChildPct (childCount, groupTotal) {
      return Math.round((childCount / Math.max(1, groupTotal)) * 100)
    },
    formatTotal (n) {
      if (!n) return '0'
      if (n >= 1000) return (n / 1000).toFixed(1) + 'K'
      return n.toLocaleString('pt-BR')
    },
    openPrimaryPicker () {
      this.pickerMode = 'primary'
      this.showPicker = true
    },
    openSecondaryPicker () {
      this.pickerMode = 'secondary'
      this.showPicker = true
    },
    closePicker () {
      this.showPicker = false
    },
    isFieldSelectedInPicker (f) {
      if (this.pickerMode === 'secondary') {
        return this.secondary &&
          this.secondary.source === f.source &&
          String(this.secondary.id) === String(f.id)
      }
      return this.primary &&
        this.primary.source === f.source &&
        String(this.primary.id) === String(f.id)
    },
    onPickField (f) {
      const ref = { source: f.source, id: f.id }
      if (this.pickerMode === 'secondary') {
        try { localStorage.setItem(CACHE_KEY_SEC, JSON.stringify(ref)) } catch (e) { /* ignora */ }
        this.$emit('change-secondary', ref)
      } else {
        try { localStorage.setItem(CACHE_KEY, JSON.stringify(ref)) } catch (e) { /* ignora */ }
        this.$emit('change-primary', ref)
      }
      this.closePicker()
    },
    onClearInPicker () {
      if (this.pickerMode === 'secondary') {
        try { localStorage.removeItem(CACHE_KEY_SEC) } catch (e) { /* ignora */ }
        this.$emit('change-secondary', null)
      } else {
        try { localStorage.removeItem(CACHE_KEY) } catch (e) { /* ignora */ }
        try { localStorage.removeItem(CACHE_KEY_SEC) } catch (e) { /* ignora */ }
        this.$emit('change-primary', null)
        this.$emit('change-secondary', null) // limpar primary derruba secondary também
      }
      this.closePicker()
    },
    onClearSecondary () {
      try { localStorage.removeItem(CACHE_KEY_SEC) } catch (e) { /* ignora */ }
      this.$emit('change-secondary', null)
    }
  }
}
</script>

<style lang="scss" scoped>
.fac-card {
  background: #fff;
  border: 1px solid #eaecf2;
  border-radius: 5px;
  padding: 22px 24px;
  box-shadow: 0 1px 3px rgba(26, 29, 38, 0.04);
}

.fac-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.fac-title {
  font-size: 14px;
  font-weight: 700;
  color: #1a1d26;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.fac-title-icon {
  color: #556ee6;
  font-size: 16px;
}

.fac-field-chip {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 5px;
  font-size: 12px;
  font-weight: 500;
  margin-left: 2px;
  border: 1px solid;
  white-space: nowrap;
}

.fac-field-chip-primary {
  background: rgba(85, 110, 230, 0.08);
  border-color: rgba(85, 110, 230, 0.2);
  color: #556ee6;
}

.fac-field-chip-secondary {
  background: rgba(139, 92, 246, 0.08);
  border-color: rgba(139, 92, 246, 0.25);
  color: #8b5cf6;
}

.fac-cross-arrow {
  color: #b0b5c8;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
}

.fac-subtitle {
  font-size: 12px;
  color: #8b90a5;
  margin: 4px 0 0 0;
  max-width: 480px;
}

.fac-header-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.fac-change-btn {
  width: 30px;
  height: 30px;
  border: 1px solid #eaecf2;
  border-radius: 6px;
  background: #fff;
  color: #8b90a5;
  cursor: pointer;
  display: grid;
  place-items: center;
  font-size: 16px;
  transition: all 0.15s ease;

  &:hover {
    color: #556ee6;
    border-color: #556ee6;
  }
}

/* Placeholder clicável (estado vazio) */
.fac-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  border: 2px dashed #d0d4e8;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: center;

  &:hover {
    border-color: #556ee6;
    background: rgba(85, 110, 230, 0.03);
  }
}

.fac-placeholder-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: rgba(85, 110, 230, 0.08);
  display: grid;
  place-items: center;
  margin-bottom: 14px;

  i {
    font-size: 26px;
    color: #556ee6;
  }
}

.fac-placeholder-text {
  font-size: 14px;
  font-weight: 600;
  color: #4a5068;
  margin-bottom: 6px;
}

.fac-placeholder-sub {
  font-size: 12px;
  color: #8b90a5;
  max-width: 360px;
  line-height: 1.5;
}

/* Loading + empty */
.fac-loading,
.fac-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  color: #8b90a5;
  font-size: 13px;
  gap: 10px;
}

.fac-loading {
  flex-direction: row;
}

.fac-spin {
  font-size: 18px;
  color: #556ee6;
  animation: fac-spin 0.9s linear infinite;
}

@keyframes fac-spin {
  to { transform: rotate(360deg); }
}

.fac-empty-icon {
  width: 48px;
  height: 48px;
  background: #f5f6fa;
  border-radius: 14px;
  display: grid;
  place-items: center;
  color: #b0b5c8;
  font-size: 22px;
}

.fac-empty-title {
  font-size: 13px;
  font-weight: 600;
  color: #4a5068;
}

.fac-empty-desc {
  font-size: 12px;
  color: #8b90a5;
  text-align: center;
  max-width: 320px;
}

/* Chart meta — total + cross button */
.fac-chart-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.fac-total-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 5px;
  font-size: 12px;
  font-weight: 500;
  background: #f5f6fa;
  color: #4a5068;

  i { color: #8b90a5; }
}

.fac-cross-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 12px;
  border: 1px dashed rgba(139, 92, 246, 0.4);
  border-radius: 5px;
  background: transparent;
  color: #8b5cf6;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  margin-left: auto;

  i { font-size: 14px; }

  &:hover {
    background: rgba(139, 92, 246, 0.06);
    border-style: solid;
  }

  &.fac-cross-btn-active {
    background: rgba(139, 92, 246, 0.08);
    border-color: rgba(139, 92, 246, 0.4);
    border-style: solid;
  }
}

/* Single mode ranking */
@keyframes facRankSlideIn {
  from { opacity: 0; transform: translateX(-8px); }
  to   { opacity: 1; transform: translateX(0); }
}

.fac-ranking {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.fac-rank-row {
  display: flex;
  align-items: center;
  gap: 12px;
  animation: facRankSlideIn 0.3s ease both;
}

.fac-rank-num {
  width: 26px;
  height: 26px;
  border-radius: 5px;
  font-size: 11px;
  font-weight: 800;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  background: #f5f6fa;
  color: #b0b5c8;

  &.fac-rank-num--gold   { background: #fef3c7; color: #d97706; }
  &.fac-rank-num--silver { background: #f1f5f9; color: #64748b; }
  &.fac-rank-num--bronze { background: #fef0e6; color: #c2692c; }
}

.fac-rank-body {
  flex: 1;
  min-width: 0;
}

.fac-rank-label {
  font-size: 13px;
  font-weight: 500;
  color: #1a1d26;
  margin-bottom: 5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.fac-rank-bar-track {
  height: 6px;
  background: #f1f3f5;
  border-radius: 99px;
  overflow: hidden;
}

.fac-rank-bar-fill {
  height: 100%;
  border-radius: 99px;
  background: linear-gradient(90deg, #556ee6, #7c8ff5);
  transition: width 0.6s cubic-bezier(0.16, 1, 0.3, 1);
}

.fac-rank-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  flex-shrink: 0;
  min-width: 60px;
}

.fac-rank-count {
  font-size: 13px;
  font-weight: 700;
  color: #1a1d26;
  line-height: 1.2;
}

.fac-rank-pct {
  font-size: 11px;
  font-weight: 500;
  color: #8b90a5;
}

/* Cross mode (hierárquico) */
.fac-cross-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.fac-cross-group {
  background: #fafbfc;
  border: 1px solid #eef0f6;
  border-radius: 8px;
  padding: 12px 14px;
  animation: facRankSlideIn 0.35s ease both;
}

.fac-cross-group-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f1f6;
}

.fac-cross-group-rank {
  width: 24px;
  height: 24px;
  border-radius: 5px;
  font-size: 11px;
  font-weight: 800;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  background: #f5f6fa;
  color: #b0b5c8;

  &.fac-rank-num--gold   { background: #fef3c7; color: #d97706; }
  &.fac-rank-num--silver { background: #f1f5f9; color: #64748b; }
  &.fac-rank-num--bronze { background: #fef0e6; color: #c2692c; }
}

.fac-cross-group-name {
  flex: 1;
  font-size: 13px;
  font-weight: 700;
  color: #1a1d26;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.fac-cross-group-count {
  font-size: 14px;
  font-weight: 800;
  color: #1a1d26;
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
}

.fac-cross-group-pct {
  font-size: 11px;
  font-weight: 600;
  color: #8b5cf6;
  background: rgba(139, 92, 246, 0.08);
  padding: 2px 6px;
  border-radius: 4px;
  flex-shrink: 0;
}

.fac-cross-children {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.fac-cross-child-row {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
}

.fac-cross-child-label {
  flex: 0 0 35%;
  color: #4a5068;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.fac-cross-child-bar {
  flex: 1;
  height: 5px;
  background: #f0f1f6;
  border-radius: 99px;
  overflow: hidden;
  min-width: 40px;
}

.fac-cross-child-fill {
  height: 100%;
  background: linear-gradient(90deg, #8b5cf6, #a78bfa);
  border-radius: 99px;
  transition: width 0.5s cubic-bezier(0.16, 1, 0.3, 1);
}

.fac-cross-child-count {
  font-size: 12px;
  font-weight: 700;
  color: #1a1d26;
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
  min-width: 28px;
  text-align: right;
}

.fac-cross-child-pct {
  font-size: 10px;
  color: #8b90a5;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
  min-width: 32px;
  text-align: right;
}

.fac-cross-more {
  font-size: 11px;
  color: #8b90a5;
  font-style: italic;
  padding: 4px 0 0;
  font-weight: 500;
}

/* Picker overlay (full-screen modal) */
.fac-picker-overlay {
  position: fixed;
  inset: 0;
  background: rgba(26, 29, 38, 0.4);
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
}

.fac-picker {
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 12px 60px rgba(26, 29, 38, 0.22);
  width: 560px;
  max-width: calc(100vw - 32px);
  max-height: 82vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.fac-picker-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px;
  border-bottom: 1px solid #f1f3f5;
  flex-shrink: 0;
  background: #fafbfc;
}

.fac-picker-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.fac-picker-header-icon {
  font-size: 18px;
  color: #556ee6;
}

.fac-picker-title {
  font-size: 15px;
  font-weight: 700;
  color: #1a1d26;
}

.fac-picker-close {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #f5f6fa;
  border: none;
  font-size: 20px;
  color: #8b90a5;
  cursor: pointer;
  display: grid;
  place-items: center;
  line-height: 1;
  transition: all 0.15s ease;

  &:hover {
    background: #eaecf2;
    color: #1a1d26;
  }
}

/* Contexto exibindo o campo primário (apenas no modal de secondary) */
.fac-picker-context {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 24px;
  background: rgba(85, 110, 230, 0.04);
  border-bottom: 1px solid #f1f3f5;
}

.fac-picker-context-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #8b90a5;
}

.fac-picker-context-chip {
  margin-left: 0;
}

.fac-picker-body {
  overflow-y: auto;
  padding: 20px 24px 24px;
}

.fac-picker-footer {
  padding: 12px 24px;
  border-top: 1px solid #f1f3f5;
  flex-shrink: 0;
}

.fac-clear-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 8px 12px;
  border: 1.5px dashed #e0b4b4;
  border-radius: 5px;
  background: #fff8f8;
  color: #c0392b;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #fef0f0;
    border-color: #c0392b;
  }
}

.fac-group {
  margin-bottom: 24px;

  &:last-child { margin-bottom: 0; }
}

.fac-group-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #8b90a5;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f1f3f5;

  i {
    font-size: 14px;
    color: #b0b5c8;
  }
}

.fac-fields-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.fac-field-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1.5px solid #eaecf2;
  border-radius: 9px;
  background: #fff;
  cursor: pointer;
  font-size: 13px;
  color: #4a5068;
  transition: all 0.12s ease;
  text-align: left;

  &:hover {
    background: #f5f6ff;
    border-color: #c5caf5;
    color: #1a1d26;
    box-shadow: 0 2px 8px rgba(85, 110, 230, 0.08);
  }

  &.selected {
    background: rgba(85, 110, 230, 0.07);
    border-color: rgba(85, 110, 230, 0.35);
    color: #3a4fa3;
    box-shadow: 0 0 0 3px rgba(85, 110, 230, 0.08);
  }
}

.fac-field-btn-label {
  flex: 1;
  font-weight: 500;
  line-height: 1.3;
}

.fac-field-check {
  font-size: 16px;
  color: #556ee6;
  flex-shrink: 0;
}

@media (max-width: 540px) {
  .fac-fields-grid { grid-template-columns: 1fr; }
  .fac-cross-child-label { flex: 0 0 30%; }
}
</style>
